import VuexPersistence from 'vuex-persist'
import Vuex from 'vuex'
import Vue from 'vue'
import { OrderWizardMutationTypes as mutations } from './mutation-types'
import { OrderWizardActionTypes as actions } from './action-types'
import { CartService } from 'smartcommerce-component-library'
import { VehicleCartProduct } from '@/shared/models/VehicleCartProduct'
import { Constants } from '../utils/Constants'
import ContractData from '../models/ContractData'
import BisnodeService from '../services/BisnodeService'
import VehicleDetails from '../models/VehicleDetails'
import { isEmpty } from '../../shared/utils/common'
import { ProductPackage } from '../models/ProductPackage'
import { webshopService } from '../../shared/services/WebshopService'
import AppConfigService from '../../shared/services/AppConfigService'

Vue.use(Vuex)

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  key: 'state_smartcommerce-fleetware-ui'
})

let productConfig = null

const initStore = async () => {
  const defaults = await AppConfigService.getConfigValue('defaults')
  return new Vuex.Store({
    state: {
      cart: {
        elements: [],
        total: 0
      },
      contractData: new ContractData(),
      billingCycleMonths: defaults ? defaults.billingCycleMonths : 12,
      selectedLoyalty: defaults ? defaults.loyaltyYears : 3,
      userData: {},
      lastOrderResponse: null,
      lastCheckBankCardPaymentResponse: null,
      vehicleDetailsList: [],
      editedVehicleDetails: null
    },

    mutations: {
      [mutations.ADD_TO_CART] (state, { productPackage, quantity }) {
        let cartProduct = cartProductFactory(productPackage, quantity)
        CartService.add(cartProduct)
        state.cart.elements = Object.assign([], CartService.elements)
        state.cart.total = CartService.total
      },

      [mutations.REMOVE_FROM_CART] (state, { productPackage, quantity }) {
        let cartProduct = cartProductFactory(productPackage, quantity)
        CartService.remove(cartProduct)
        state.cart.elements = Object.assign([], CartService.elements)
        state.cart.total = CartService.total
      },

      [mutations.UPDATE_CART] (state) {
        state.cart.elements = Object.assign([], CartService.elements)
        state.cart.total = CartService.total
      },

      [mutations.UPDATE_BILLING_CYCLE] (state, val) {
        state.billingCycleMonths = val
      },

      [mutations.CLEAR_CART] (state) {
        CartService.clear()
        state.cart.elements = []
        state.cart.total = CartService.total
      },

      [mutations.SAVE_CONTRACT_DATA] (state, contractData) {
        state.contractData = contractData
      },

      [mutations.CLEAR_CONTRACT_DATA] (state) {
        state.contractData = new ContractData()
      },

      [mutations.UPDATE_SELECTED_LOYALTY] (state, val) {
        state.selectedLoyalty = val
      },

      [mutations.RESET_SELECTED_LOYALTY] (state) {
        state.selectedLoyalty = 3
      },

      [mutations.SET_USER_DATA] (state, userData) {
        state.userData = userData
      },

      [mutations.SET_LAST_ORDER_RESPONSE] (state, response) {
        state.lastOrderResponse = response
      },

      [mutations.CLEAR_USER_DATA] (state) {
        state.userData = {}
      },

      [mutations.SET_LAST_CHECK_BANK_CARD_PAYMENT_RESPONSE] (state, response) {
        state.lastCheckBankCardPaymentResponse = response
      },

      [mutations.ADD_VEHICLE] (state, { productPackage, quantity }) {
        for (let i = 0; i < quantity; i++) {
          state.vehicleDetailsList.push(new VehicleDetails({
            groupCode: productPackage.groupCode
          }))
        }
      },

      [mutations.REMOVE_VEHICLE] (state, { productPackage, quantity }) {
        for (let i = 0; i < quantity; i++) {
          for (let j = state.vehicleDetailsList.length - 1; j > -1; j--) {
            if (state.vehicleDetailsList[j].groupCode === productPackage.groupCode) {
              state.vehicleDetailsList.splice(j, 1)
              break
            }
          }
        }
      },

      [mutations.UPDATE_EDITED_VEHICLE_DETAILS_ITEM] (state, editedVehicle) {
        state.editedVehicleDetails = editedVehicle
      },

      [mutations.STORE_EDITED_VEHICLE_DETAILS] (state, editedVehicle) {
        state.editedVehicleDetails = null
        for (let i = 0; i < state.vehicleDetailsList.length; i++) {
          let vehicle = state.vehicleDetailsList[i]
          if (vehicle.id === editedVehicle.id) {
            state.vehicleDetailsList[i] = editedVehicle
            return
          }
        }
      },

      [mutations.CLEAR_VEHICLES] (state) {
        state.vehicleDetailsList = []
        state.editedVehicleDetails = null
      }
    },

    actions: {
      [actions.LOYALTY_CHANGE] ({ getters, commit }, { loyalty, possibleProductPackages }) {
        var products = getters.getProductsInCart()
        if (products.length === 0) return
        for (var i = 0; i < possibleProductPackages.length; i++) {
          var product = possibleProductPackages[i]
          var stateProduct = findInArrayById(products, product.id)
          if (!stateProduct) continue
          else {
            stateProduct.product = product
            stateProduct.price = product.getTotalNetPrice()
            stateProduct.product.loyaltyLength = loyalty
            CartService.updateProduct(stateProduct)
          }
        }
        commit(mutations.UPDATE_CART)
      },

      [actions.INIT_CART] ({ commit }) {
        commit(mutations.UPDATE_CART)
      },

      [actions.ADD_TO_CART] ({ commit }, { productPackage, quantity }) {
        commit(mutations.ADD_TO_CART, { productPackage, quantity })
        commit(mutations.ADD_VEHICLE, { productPackage, quantity })
      },

      [actions.REMOVE_FROM_CART] ({ commit }, { productPackage, quantity }) {
        commit(mutations.REMOVE_FROM_CART, { productPackage, quantity })
        commit(mutations.REMOVE_VEHICLE, { productPackage, quantity })
      },

      [actions.CLEAR_CART] ({ commit }) {
        commit(mutations.CLEAR_CART)
        commit(mutations.CLEAR_VEHICLES)
      },

      [actions.COMPANY_SEARCH] (state, name) {
        return BisnodeService.search(name)
      },

      async [actions.GET_PRODUCT_CONFIGURATION] () {
        if (productConfig === null) {
          productConfig = await webshopService.getProductConfig()
        }

        return productConfig
      },

      async [actions.UPDATE_LOYALTY] ({ dispatch, commit, state }, loyalty) {
        if (state.selectedLoyalty === loyalty) return
        commit(mutations.UPDATE_SELECTED_LOYALTY, loyalty)
        const productConfig = await dispatch(actions.GET_PRODUCT_CONFIGURATION)
        dispatch(actions.LOYALTY_CHANGE, {
          loyalty,
          possibleProductPackages: getProductPackages(productConfig.productGroups, loyalty)
        })
      },

      async [actions.POST_ORDER] ({ state }) {
        const contractData = state.contractData
        const cartProducts = state.cart.elements
        const billingCycle = state.billingCycleMonths === 1 ? 'MONTHLY' : 'ANNUALLY'
        const loyaltyYears = state.selectedLoyalty
        const userData = state.userData
        const vehicleDetailsList = state.vehicleDetailsList
        cartProducts.forEach(v => delete v.$$vehicleDetails)
        for (let i = 0; i < vehicleDetailsList.length; i++) {
          let vehicleDetails = new VehicleDetails(vehicleDetailsList[i])
          if (isEmpty(vehicleDetails.groupCode) || isEmpty(vehicleDetails.packageCode)) continue
          for (let j = 0; j < cartProducts.length; j++) {
            let product = cartProducts[j]
            if (vehicleDetails.groupCode === product.groupCode && vehicleDetails.packageCode === product.code) {
              product.$$vehicleDetails = product.$$vehicleDetails || []
              product.$$vehicleDetails.push(vehicleDetails)
              break
            }
          }
        }
        return webshopService.postOrder({ contractData, cartProducts, billingCycle, loyaltyYears, userData })
      },

      [actions.POST_ORDER_CLEANUP] ({ dispatch, commit }) {
        dispatch(actions.CLEAR_CART)
        commit(mutations.CLEAR_CONTRACT_DATA)
        commit(mutations.RESET_SELECTED_LOYALTY)
        commit(mutations.CLEAR_USER_DATA)
        commit(mutations.UPDATE_BILLING_CYCLE, 1)
      }
    },

    getters: {
      getCountOfProductInCart: (state, getters) => (product) => {
        let products = getters.getProductsInCart()
        for (let item in products) {
          let stateProduct = products[item]
          if (stateProduct.id !== product.id) continue
          return stateProduct.quantity
        }

        return 0
      },

      getProductsInCartMappedByGroupAndPackage: (state) => () => {
        var groups = {}
        var products = state.cart.elements
        for (var item in products) {
          var stateProduct = products[item]
          groups[stateProduct.groupCode] = groups[stateProduct.groupCode] || {}
          groups[stateProduct.groupCode][stateProduct.code] = groups[stateProduct.groupCode][stateProduct.code] || []
          groups[stateProduct.groupCode][stateProduct.code].push(new VehicleCartProduct(stateProduct))
        }
        return groups
      },

      getProductsInCart: (state) => () => {
        return state.cart.elements.map(e => new VehicleCartProduct(e))
      },

      getDiscountsInCart: (state) => () => {
        let list = []
        if (state.billingCycleMonths === 12) {
          list.push({
            discountPercent: Constants.YEARLY_PAYMENT_DISCOUNT,
            code: 'yearly_payment'
          })
        }
        return list
      },

      getDiscountMultiplier: (state, getters) => () => {
        return getters.getDiscountsInCart().reduce((acc, val) => acc - parseFloat(val.discountPercent), 1)
      },

      isCartEmpty: () => () => {
        return CartService.getElements().length === 0
      },

      getTotalNetCost: (state, getters) => () => {
        let discounts = getters.getDiscountMultiplier()
        return getters.getProductsInCart().reduce((acc, p) => acc + p.product.getTotalNetPrice(state.billingCycleMonths, discounts) * p.quantity, 0)
      },

      getTotalGrossCost: (state, getters) => () => {
        let discounts = getters.getDiscountMultiplier()
        let grossTotal = getters.getProductsInCart().reduce((acc, p) => acc + p.product.getTotalGrossPrice(state.billingCycleMonths, discounts) * p.quantity, 0)
        return grossTotal
      },

      getTotalGrossCostWithoutDiscounts: (state, getters) => (billingCycleMonths = state.billingCycleMonths, discountMultiplier = 1) => {
        return getters.getProductsInCart().reduce((acc, p) => acc + p.product.getTotalGrossPrice(billingCycleMonths, discountMultiplier) * p.quantity, 0)
      },

      getCartNetTotalWithoutDiscounts: (state, getters) => () => {
        return getters.getProductsInCart().reduce((acc, p) => acc + p.product.getTotalNetPrice(state.billingCycleMonths) * p.quantity, 0)
      },

      getCartNetMonthlyFeesWithoutDiscount: (state, getters) => (billingCycleMonths = state.billingCycleMonths) => {
        return getters.getProductsInCart().reduce((acc, p) => acc + p.product.monthlyFee * billingCycleMonths * p.quantity, 0)
      },

      getTotalVatCost: (state, getters) => () => {
        let discounts = getters.getDiscountMultiplier()
        let vatTotal = getters.getProductsInCart().reduce((acc, p) => acc + p.product.getTotalGrossPrice(state.billingCycleMonths, discounts) * p.quantity - p.product.getTotalNetPrice(state.billingCycleMonths, discounts) * p.quantity, 0)
        return vatTotal
      },

      getIndexOfVehicleInGroup: (state, getters) => (details) => {
        let vehiclesInGroup = getters.getVehiclesInCart().filter(v => v.groupCode === details.groupCode)
        return vehiclesInGroup.findIndex(v => v.id === details.id)
      },

      getVehiclesInCart: (state) => () => {
        return state.vehicleDetailsList.map(v => new VehicleDetails(v))
      },

      numberOfUnfilledVehiclesInCart: (state, getters) => () => {
        return getters.getVehiclesInCart().filter(v => !v.isFilled()).length
      }
    },

    plugins: [vuexLocal.plugin]
  })
}

export const store = initStore()

export function cartProductFactory (product, quantity) {
  product = new ProductPackage(product)
  return new VehicleCartProduct({
    id: product.id,
    name: product.name,
    product,
    quantity,
    price: product.getTotalNetPrice(),
    groupCode: product.groupCode,
    code: product.code,
    addons: product.addons
  })
}

function findInArrayById (items, id) {
  for (var i = 0; i < items.length; i++) {
    var item = items[i]
    if (item.id !== id) continue
    return item
  }
}

function getProductPackages (groups, loyalty) {
  let productPackages = []

  groups.forEach(group => {
    group.packages.forEach(productPackage => {
      if (productPackage.loyaltyLength === loyalty) productPackages.push(productPackage)
    })
  })

  return productPackages
}
